import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { apiPost, apiPut } from "../../utils/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { AdminFormDialog, AdminField } from "./AdminFormDialog";
import { Input, Textarea } from "../ui/input";
import { DocumentUpload } from "../subcomponents/DocumentUpload";
import { RichEditor } from "../subcomponents/RichEditor";
import { TENDER_CATEGORY } from "../../utils/tenders";

type TenderFormState = {
  title: string;
  excerpt: string;
  content: string;
  eventDate: string;
  attachmentUrl: string;
  coverImage: string;
};

const EMPTY: TenderFormState = {
  title: "",
  excerpt: "",
  content: "",
  eventDate: "",
  attachmentUrl: "",
  coverImage: "",
};

type Props = {
  open: boolean;
  editing: any | null;
  isAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const FORM_ID = "tender-form";

export const AdminTenderModal: React.FC<Props> = ({ open, editing, isAdmin, onClose, onSaved }) => {
  const { t } = useLanguage();

  const schema = Yup.object({
    title: Yup.string().trim().required(t.common.required),
    excerpt: Yup.string(),
    content: Yup.string().trim().required(t.common.required),
    eventDate: Yup.string(),
    attachmentUrl: Yup.string(),
    coverImage: Yup.string(),
  });

  const initial: TenderFormState = editing
    ? {
        title: editing.title || "",
        excerpt: editing.excerpt || "",
        content: editing.content || "",
        eventDate: editing.eventDate ? editing.eventDate.slice(0, 16) : "",
        attachmentUrl:
          editing.location && /^https?:\/\//i.test(editing.location) ? editing.location : "",
        coverImage: editing.coverImage || "",
      }
    : EMPTY;

  const submitLabel = editing ? t.admin.save : isAdmin ? t.admin.publish : t.admin.submitForReview;

  return (
    <Formik
      initialValues={initial}
      validationSchema={schema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        // Même API que les articles : type `article` + catégorie `appel-offre`
        const payload = {
          title: values.title.trim(),
          excerpt: values.excerpt.trim(),
          content: values.content.trim(),
          category: TENDER_CATEGORY,
          coverImage: values.coverImage,
          eventDate: values.eventDate || null,
          location: values.attachmentUrl.trim() || null,
          type: "article" as const,
        };
        try {
          if (editing) {
            await apiPut(`/contents/${editing.id}`, payload);
            toast.success(t.common.saveSuccess);
          } else {
            await apiPost("/contents", payload);
            toast.success(isAdmin ? t.admin.published : t.admin.submitted);
          }
          onSaved();
          onClose();
        } catch (e: any) {
          toast.error(e?.response?.data?.reason || e?.messageText || t.common.error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <AdminFormDialog
          open={open}
          onClose={onClose}
          title={editing ? t.admin.editTender : t.admin.newTender}
          subtitle={t.admin.tenderSubtitle}
          testId="tender-modal"
          className="max-w-3xl"
          formId={FORM_ID}
          submitLabel={submitLabel}
          isSubmitting={isSubmitting}
        >
          <Form id={FORM_ID} className="grid gap-4 pb-4">
            <AdminField label={t.admin.contentTitle} required error={touched.title ? errors.title : undefined}>
              <Field as={Input} name="title" data-testid="tender-title" />
            </AdminField>
            <AdminField label={t.admin.tenderDeadline}>
              <Field as={Input} name="eventDate" type="datetime-local" data-testid="tender-deadline" />
            </AdminField>
            <AdminField label={t.admin.excerpt}>
              <Field as={Textarea} name="excerpt" rows={2} />
            </AdminField>
            <DocumentUpload
              value={values.attachmentUrl}
              onChange={(url) => setFieldValue("attachmentUrl", url)}
              folder="ulpgl/uploads"
              label={t.admin.tenderDocument}
              testId="tender-attachment"
            />
            <DocumentUpload
              value={values.coverImage}
              onChange={(url) => setFieldValue("coverImage", url)}
              folder="ulpgl/uploads"
              label={t.admin.coverImage}
              testId="tender-cover"
            />
            <AdminField label={t.admin.contentBody} required error={touched.content ? errors.content : undefined}>
              <div data-testid="tender-content">
                <RichEditor
                  value={values.content}
                  onChange={(html) => setFieldValue("content", html)}
                  folder="ulpgl/uploads"
                />
              </div>
            </AdminField>
          </Form>
        </AdminFormDialog>
      )}
    </Formik>
  );
};
